/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { View } from '@components/atoms'
import { Footer } from '@components/systems'
import { customerAuthStyle } from './customerAuth.style'

import { useNavigate } from 'react-router-dom'
import { useAppSelector, useAppDispatch, customerAuthActions } from '@redux/index'
import { produce } from 'immer'

/**
 * Membership(가입) — Monolith(2안).
 *
 * 필드 구성·검증·redux(JOIN) 흐름은 구 페이지 그대로. 표기만 §6.4 폼 규격으로 옮겼다.
 * 뉴스레터 동의 체크박스는 MUI Checkbox 대신 monoForm 의 .mnCheck (헤어라인 사각형).
 */
const MonolithCustomerJoinPage = () => {
	// redux dispatch
	const reduxDispatch = useAppDispatch()

	// hook
	const navigate = useNavigate()

	// redux state
	const joinErrorMessage: string = useAppSelector(
		state => state.customerAuth.joinErrorMessage,
	)

	// state
	const [account, setAccount] = React.useState({
		name: '',
		email: '',
		confirmEmailNewsYn: true,
		password: '',
		confirmPassword: '',
	})

	const update = (patch: Partial<typeof account>) => {
		const newAccount = produce(account, draft => {
			Object.assign(draft, patch)
		})

		setAccount(newAccount)
	}

	const onJoinClick = React.useCallback(() => {
		if (
			account.name !== '' &&
			account.email !== '' &&
			account.password !== '' &&
			account.confirmPassword !== ''
		) {
			reduxDispatch(customerAuthActions.JOIN({ ...account }))
		} else {
			reduxDispatch(
				customerAuthActions.JOIN_ERROR_MESSAGE(
					'Please check your join information again.',
				),
			)
		}
	}, [account])

	const onSubmit = (e: React.FormEvent) => {
		e.preventDefault()
		onJoinClick()
	}

	return (
		<View css={style}>
			<View className="mnWrap">
				<View className="mnAuthHeader">
					<h1 className="mnAuthTitle">Membership</h1>
					<p className="mnAuthLede">
						One account for orders, shipping and announcements.
					</p>
				</View>

				<form className="mnAuthForm" onSubmit={onSubmit} noValidate>
					<View className="mnField">
						<label className="mnLabel" htmlFor="mnJoinName">
							Your name
						</label>
						<input
							id="mnJoinName"
							type="text"
							className="mnInput"
							value={account.name}
							onChange={e => update({ name: e.target.value })}
							autoComplete="name"
						/>
					</View>

					<View className="mnField">
						<label className="mnLabel" htmlFor="mnJoinEmail">
							Email
						</label>
						<input
							id="mnJoinEmail"
							type="email"
							className="mnInput"
							value={account.email}
							onChange={e => update({ email: e.target.value })}
							autoComplete="email"
						/>

						<button
							type="button"
							className="mnCheck"
							aria-pressed={account.confirmEmailNewsYn}
							onClick={() => update({ confirmEmailNewsYn: !account.confirmEmailNewsYn })}
						>
							<span
								className={`mnCheckBox ${account.confirmEmailNewsYn ? 'on' : ''}`}
								aria-hidden="true"
							/>
							<span className="mnCheckLabel">
								Yes, I’d like to receive updates on the latest guitar gear and discounts.
							</span>
						</button>
					</View>

					<View className="mnField">
						<label className="mnLabel" htmlFor="mnJoinPassword">
							Password
						</label>
						<input
							id="mnJoinPassword"
							type="password"
							className="mnInput"
							value={account.password}
							onChange={e => update({ password: e.target.value })}
							autoComplete="new-password"
						/>
					</View>

					<View className="mnField">
						<label className="mnLabel" htmlFor="mnJoinConfirmPassword">
							Confirm password
						</label>
						<input
							id="mnJoinConfirmPassword"
							type="password"
							className="mnInput"
							value={account.confirmPassword}
							onChange={e => update({ confirmPassword: e.target.value })}
							autoComplete="new-password"
						/>
					</View>

					{joinErrorMessage !== '' && <span className="mnError">{joinErrorMessage}</span>}

					<View className="mnAuthAction">
						<button type="submit" className="mnCta mnAuthCta">
							Create account
						</button>
					</View>

					<View className="mnAuthLinks">
						<button
							type="button"
							className="mnAuthLink"
							onClick={() => navigate('/customer/login')}
						>
							Back to login
						</button>
					</View>
				</form>
			</View>

			<Footer />
		</View>
	)
}

export default MonolithCustomerJoinPage

const style = css(customerAuthStyle)
